import styled from "@emotion/styled";
import { ToggleGroup as RadixToggleGroup } from "radix-ui";
import type React from "react";

import { theme } from "@themes/themes";

import { ToggleGroupItem } from "./ToggleGroupItem";

type ToggleOption<V extends string = string> = {
  label: string | React.ReactNode;
  value: V;
} & React.ComponentPropsWithoutRef<"button">;

interface ToggleGroupProps<V extends string = string> {
  options: ToggleOption<V>[];
  value: V;
  onChange: (value: V) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  ItemComponent?: React.ComponentType<React.ComponentProps<typeof ToggleGroupItem> & { value: V }>;
  variant?: "primary" | "secondary";
  size?: "sm" | "md";
}

export const ToggleGroup = <V extends string = string>({
  options,
  value,
  onChange,
  fullWidth = false,
  disabled = false,
  ItemComponent = ToggleGroupItem,
  variant = "primary",
  size = "md",
}: ToggleGroupProps<V>) => {
  const handleOnChange = (newValue: string) => {
    if (!newValue) return;
    onChange(newValue as V);
  };

  return (
    <StyledRoot
      {...(fullWidth && { "data-full-width": true })}
      disabled={disabled}
      type="single"
      value={value}
      variant={variant}
      onValueChange={handleOnChange}
    >
      {options.map((option) => {
        const { label, value, ...rest } = option;

        return (
          <ItemComponent
            key={value}
            aria-label={typeof label === "string" ? label : undefined}
            data-color-value={value}
            disabled={disabled}
            size={size}
            value={value}
            {...rest}
          >
            {label}
          </ItemComponent>
        );
      })}
    </StyledRoot>
  );
};

const StyledRoot = styled(RadixToggleGroup.Root)<{ variant: "primary" | "secondary" }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;

  gap: 4px;
  margin: 0;
  padding: 0;

  width: ${(props) => ((props as any)["data-full-width"] ? "100%" : "fit-content")};
  height: auto;

  background: ${theme.color("gray-4")};
  border: 1px solid ${theme.color("gray-5")};
  border-radius: 8px;

  box-shadow: none;
  overflow: visible;

  ${({ variant }) =>
    variant === "secondary" &&
    `
    background: transparent;
    border: none;
  `};
`;
